import config from "../../../../../config";
import customToast from "component/Toastr";
import { sweatalert } from "component/Sweatalert2";
import {
  AddAppointmentForm,
  AppointmentDetailModal,
  RescheduleAppointmentForm,
  ShowLoadingSpinner,
  appointmentDetailApi,
} from "store/slices/appointmentSlice";
import { AddBusytimeForm, EditBusytimeForm } from "store/slices/busytimeSlice";
import { NotificationToCalendar, UpcomingNotifyDetailDrawer } from "store/slices/notificationSlice";
import { ClientDetailApi, ClientDetailTab, OpenClientDetailModal } from "store/slices/clientSlice";
import {
  ClientInvoiceDetailApi,
  ClientInvoiceListViewApi,
  OpenClientInvoiceDetails,
  OpenPartialPaidInvoice,
  InvoiceRedirectLoading,
} from "store/slices/clientinvoiceSlice";

export function isCalendarRoute(pathname) {
  const calendarPath = `${config.basePath || ""}/calendar`.replace(/\/{2,}/g, "/");
  return pathname === "/calendar" || pathname === calendarPath;
}

export function getCalendarPath() {
  return `${config.basePath || ""}/calendar`.replace(/\/{2,}/g, "/");
}

export function buildNotificationCalendarPayload(notificationItem) {
  const appt = notificationItem?.appointment || {};
  const clientId = appt?.client?.id || notificationItem?.client_id || appt?.client_id || "";

  return {
    ...notificationItem,
    appointment: {
      ...appt,
      id: appt?.id || notificationItem?.appointment_id || "",
      client: appt?.client?.id ? appt.client : { ...(appt?.client || {}), id: clientId },
    },
  };
}

export function extractApiErrorMessage(action, fallback = "Unable to open this appointment.") {
  const payload = action?.payload;
  const nested = payload?.message;

  if (typeof nested === "string" && nested.trim()) {
    return nested;
  }

  if (nested?.errors?.document?.[0]) {
    return nested.errors.document[0];
  }

  if (typeof nested?.message === "string" && nested.message.trim()) {
    return nested.message;
  }

  if (typeof payload?.response?.data?.message === "string") {
    return payload.response.data.message;
  }

  if (typeof action?.error?.message === "string" && action.error.message.trim()) {
    return action.error.message;
  }

  return fallback;
}

function showAppointmentDetailError(action, t) {
  const payload = action?.payload;
  const nested = payload?.message;
  const documentErrors = nested?.errors?.document;

  if (payload?.status === 422 && Array.isArray(documentErrors) && documentErrors.length > 0) {
    sweatalert({
      title: documentErrors[0],
      text: documentErrors,
      icon: "error",
    });
    return;
  }

  customToast.error(extractApiErrorMessage(action, t("Unable to open this appointment.")));
}

export async function openNotificationAppointmentOnCalendar({
  dispatch,
  t,
  notificationItem,
  singleStaffAccess = "",
}) {
  const payload = buildNotificationCalendarPayload(notificationItem);
  const appt = payload?.appointment || {};
  const appointmentId = appt?.id || notificationItem?.appointment_id || "";
  const clientId = appt?.client?.id || notificationItem?.client_id || appt?.client_id || "";

  if (!appointmentId) {
    customToast.error(t("Unable to open this appointment."));
    dispatch(NotificationToCalendar(""));
    return;
  }

  dispatch(ShowLoadingSpinner(true));
  dispatch(AddAppointmentForm(""));
  dispatch(AddBusytimeForm(""));
  dispatch(EditBusytimeForm(""));
  dispatch(RescheduleAppointmentForm(""));

  try {
    const action = await dispatch(
      appointmentDetailApi({
        id: appointmentId,
        client_id: clientId,
        showdate: appt?.dateof || "",
        showstartdate: appt?.start_datetime || "",
        showenddate: appt?.end_datetime || "",
        page_name: "calendar",
        singlestaffaccess: singleStaffAccess,
      }),
    );

    if (action.meta.requestStatus === "fulfilled") {
      dispatch(AppointmentDetailModal(true));
      setTimeout(() => {
        dispatch(AppointmentDetailModal("open"));
      }, config.modalOpenTimeOut);
    } else if (action.meta.requestStatus === "rejected") {
      showAppointmentDetailError(action, t);
    }
  } catch (error) {
    customToast.error(t("Sorry there was a problem, please refresh your browser"));
  } finally {
    dispatch(ShowLoadingSpinner(false));
    dispatch(NotificationToCalendar(""));
  }
}

export async function openOnlineBookingNotification({
  dispatch,
  navigate,
  location,
  t,
  notificationItem,
  singleStaffAccess = "",
}) {
  const payload = buildNotificationCalendarPayload(notificationItem);
  const appt = payload?.appointment || {};
  const appointmentId = appt?.id || notificationItem?.appointment_id || "";
  const clientId = appt?.client?.id || notificationItem?.client_id || appt?.client_id || "";

  if (!appointmentId) {
    customToast.error(t("Unable to open this appointment."));
    return;
  }

  dispatch(UpcomingNotifyDetailDrawer(""));

  const calendarPath = getCalendarPath();
  const onCalendar = isCalendarRoute(location?.pathname || "");

  if (!onCalendar) {
    dispatch(NotificationToCalendar(payload));
    navigate(calendarPath);
    return;
  }

  dispatch(NotificationToCalendar(""));
  return openNotificationAppointmentOnCalendar({
    dispatch,
    t,
    notificationItem: payload,
    singleStaffAccess,
  });
}

/** Open client invoice drawer so staff can process a manual refund after portal cancel. */
export async function openCancelledBookingInvoice({ dispatch, t, notificationItem }) {
  const appt = notificationItem?.appointment || {};
  const clientId =
    appt?.client?.id ||
    notificationItem?.client_id ||
    appt?.client_id ||
    appt?.sale?.client_id ||
    "";
  const saleId = appt?.sale_id || appt?.sale?.id || "";

  if (!clientId || !saleId) {
    customToast.error(t("Unable to open this invoice."));
    dispatch(InvoiceRedirectLoading(false));
    return;
  }

  dispatch(InvoiceRedirectLoading(true));
  dispatch(OpenClientInvoiceDetails(""));
  dispatch(OpenPartialPaidInvoice(""));
  dispatch(ClientDetailTab("invoices"));

  try {
    const detailAction = await dispatch(ClientDetailApi({ id: clientId }));
    if (detailAction.meta.requestStatus !== "fulfilled") {
      customToast.error(t("Unable to open this invoice."));
      return;
    }

    dispatch(ClientInvoiceListViewApi({ client_id: clientId }));
    dispatch(OpenClientDetailModal(true));
    setTimeout(() => {
      dispatch(OpenClientDetailModal("open"));
    }, config.modalOpenTimeOut);
    dispatch(UpcomingNotifyDetailDrawer(""));

    const invoiceAction = await dispatch(
      ClientInvoiceDetailApi({ client_id: clientId, id: saleId })
    );

    if (invoiceAction.meta.requestStatus !== "fulfilled") {
      customToast.error(t("Unable to open this invoice."));
      return;
    }

    const sale = invoiceAction.payload || {};
    const status = sale.status || "";
    const isDepositRefund = sale.is_deposit_refund === 1;

    if (status === "Paid" || status === "Schedule") {
      dispatch(OpenClientInvoiceDetails("open"));
    } else if (status === "Partial" || isDepositRefund) {
      dispatch(OpenPartialPaidInvoice("open"));
    } else {
      dispatch(OpenClientInvoiceDetails("open"));
    }
  } catch (error) {
    customToast.error(t("Sorry there was a problem, please refresh your browser"));
  } finally {
    dispatch(InvoiceRedirectLoading(false));
  }
}
